/**
 * @file Pure reducer and session helpers for upload manager React state.
 * Handles session/file CRUD, sorting, retention pruning, and aggregate progress.
 */

import { UPLOAD_SESSION_RETENTION_MS } from './constants';
import {
  MAX_UNDISMISSED_COMPLETED_SESSIONS,
  UPLOAD_IN_PROGRESS_STATUSES,
  type UploadFileState,
  type UploadFileStatus,
  type UploadResourceSession,
} from './types';
import { isSessionSettled } from './upload-session-outcome';

/** Reducer state shape. */
export type UploadManagerState = {
  sessions: UploadResourceSession[];
};

/** Discriminated union of upload manager state transitions. */
export type UploadManagerAction =
  | {
      type: 'ENSURE_SESSION';
      session: Omit<UploadResourceSession, 'files'> & {
        files?: UploadFileState[];
      };
    }
  | {
      type: 'ADD_FILE';
      resourceKey: string;
      file: UploadFileState;
    }
  | {
      type: 'UPDATE_FILE';
      uppyFileId: string;
      patch: Partial<UploadFileState>;
    }
  | {
      type: 'REMOVE_FILE';
      uppyFileId: string;
    }
  | {
      type: 'DISMISS_SESSION';
      resourceKey: string;
    }
  | {
      type: 'MARK_SESSION_COMPLETE';
      resourceKey: string;
      completedAt: string;
    }
  | {
      type: 'HYDRATE_SESSIONS';
      sessions: UploadResourceSession[];
    }
  | { type: 'PRUNE' };

const upsertSession = (
  sessions: UploadResourceSession[],
  session: UploadResourceSession
): UploadResourceSession[] => {
  const index = sessions.findIndex((s) => s.id === session.id);
  if (index === -1) {
    return [...sessions, session];
  }
  const next = [...sessions];
  next[index] = session;
  return next;
};

const updateFileInSessions = (
  sessions: UploadResourceSession[],
  uppyFileId: string,
  patch: Partial<UploadFileState>
): UploadResourceSession[] =>
  sessions.map((session) => {
    const fileIndex = session.files.findIndex(
      (f) => f.uppyFileId === uppyFileId
    );
    if (fileIndex === -1) {
      return session;
    }
    const files = [...session.files];
    files[fileIndex] = { ...files[fileIndex], ...patch };
    return { ...session, files };
  });

const removeFileFromSessions = (
  sessions: UploadResourceSession[],
  uppyFileId: string
): UploadResourceSession[] =>
  sessions
    .map((session) => ({
      ...session,
      files: session.files.filter((f) => f.uppyFileId !== uppyFileId),
    }))
    .filter((session) => session.files.length > 0 || !session.dismissed);

const hasActiveFiles = (session: UploadResourceSession) =>
  session.files.some((file) =>
    (['queued', 'uploading', 'paused', 'error'] as UploadFileStatus[]).includes(
      file.status
    )
  );

/**
 * Whether a completed session is still within the retention window.
 * Active/incomplete sessions always return true.
 */
export const isUploadSessionWithinRetention = (
  session: UploadResourceSession,
  now = Date.now(),
  retentionMs = UPLOAD_SESSION_RETENTION_MS
): boolean => {
  // Keep active / incomplete sessions regardless of age.
  if (session.completedAt == null) {
    return true;
  }
  return now - new Date(session.completedAt).getTime() <= retentionMs;
};

/** Sorts sessions: active first, then by most recent completion time. */
export const sortSessions = (
  sessions: UploadResourceSession[]
): UploadResourceSession[] =>
  [...sessions].sort((a, b) => {
    const aActive = hasActiveFiles(a);
    const bActive = hasActiveFiles(b);
    if (aActive !== bActive) {
      return aActive ? -1 : 1;
    }
    const aTime = a.completedAt ?? '';
    const bTime = b.completedAt ?? '';
    return bTime.localeCompare(aTime);
  });

/**
 * Removes dismissed and expired sessions; soft-caps completed history.
 * @internal Called after dismiss and on hydrate.
 */
export const pruneSessions = (
  sessions: UploadResourceSession[],
  now = Date.now()
): UploadResourceSession[] => {
  const withoutDismissed = sessions.filter((session) => !session.dismissed);
  const withinRetention = withoutDismissed.filter((session) =>
    isUploadSessionWithinRetention(session, now)
  );

  const completedUndismissed = withinRetention.filter(
    (session) => session.completedAt != null
  );
  const activeOrIncomplete = withinRetention.filter(
    (session) => session.completedAt == null
  );

  // Newest first via sortSessions — then soft-cap completed history.
  const cappedCompleted = sortSessions(completedUndismissed).slice(
    0,
    MAX_UNDISMISSED_COMPLETED_SESSIONS
  );

  return sortSessions([...activeOrIncomplete, ...cappedCompleted]);
};

/** Count of files currently queued, uploading, or paused across all sessions. */
export const countActiveUploads = (sessions: UploadResourceSession[]): number =>
  sessions.reduce(
    (count, session) =>
      count +
      session.files.filter((file) =>
        UPLOAD_IN_PROGRESS_STATUSES.includes(file.status)
      ).length,
    0
  );

/** Count of non-dismissed sessions (used for panel empty state). */
export const countVisibleUploadSessions = (
  sessions: UploadResourceSession[]
): number => sessions.filter((session) => !session.dismissed).length;

/**
 * Prefers live in-progress sessions over hydrated snapshots when the user enqueued
 * during bootstrap recovery.
 */
export const mergeHydratedWithBootEnqueues = (
  hydrated: UploadResourceSession[],
  current: UploadResourceSession[],
  bootEnqueuedSessionIds: Set<string>
): UploadResourceSession[] => {
  if (bootEnqueuedSessionIds.size === 0) {
    return hydrated;
  }

  const hydratedById = new Map(
    hydrated.map((session) => [session.id, session])
  );
  const currentById = new Map(current.map((session) => [session.id, session]));
  const allIds = new Set([...hydratedById.keys(), ...currentById.keys()]);

  return Array.from(allIds).map((id) => {
    if (bootEnqueuedSessionIds.has(id) && currentById.has(id)) {
      const currentSession = currentById.get(id)!;
      const hasInProgress = currentSession.files.some((file) =>
        UPLOAD_IN_PROGRESS_STATUSES.includes(file.status)
      );
      if (hasInProgress) {
        return currentSession;
      }
    }

    return hydratedById.get(id) ?? currentById.get(id)!;
  });
};

/** Main upload session reducer used by {@link UploadsProvider}. */
export const uploadManagerReducer = (
  state: UploadManagerState,
  action: UploadManagerAction
): UploadManagerState => {
  switch (action.type) {
    case 'ENSURE_SESSION': {
      const existing = state.sessions.find((s) => s.id === action.session.id);
      const session: UploadResourceSession = {
        ...action.session,
        files: action.session.files ?? existing?.files ?? [],
        dismissed: false,
        completedAt: undefined,
        meta: action.session.meta ?? existing?.meta ?? {},
      };
      return {
        sessions: sortSessions(upsertSession(state.sessions, session)),
      };
    }
    case 'ADD_FILE': {
      const session = state.sessions.find((s) => s.id === action.resourceKey);
      if (!session) {
        return state;
      }
      return {
        sessions: sortSessions(
          upsertSession(state.sessions, {
            ...session,
            dismissed: false,
            completedAt: undefined,
            files: [...session.files, action.file],
          })
        ),
      };
    }
    case 'UPDATE_FILE': {
      let sessions = updateFileInSessions(
        state.sessions,
        action.uppyFileId,
        action.patch
      );

      sessions = sessions.map((session) => {
        if (isSessionSettled(session) && !session.completedAt) {
          return {
            ...session,
            completedAt: new Date().toISOString(),
          };
        }
        return session;
      });

      return { sessions: sortSessions(sessions) };
    }
    case 'REMOVE_FILE':
      return {
        sessions: sortSessions(
          removeFileFromSessions(state.sessions, action.uppyFileId)
        ),
      };
    case 'DISMISS_SESSION':
      return {
        sessions: pruneSessions(
          state.sessions.map((session) =>
            session.id === action.resourceKey
              ? { ...session, dismissed: true }
              : session
          )
        ),
      };
    case 'MARK_SESSION_COMPLETE':
      return {
        sessions: sortSessions(
          state.sessions.map((session) =>
            session.id === action.resourceKey
              ? { ...session, completedAt: action.completedAt }
              : session
          )
        ),
      };
    case 'HYDRATE_SESSIONS':
      return { sessions: pruneSessions(sortSessions(action.sessions)) };
    case 'PRUNE':
      return { sessions: pruneSessions(state.sessions) };
    default:
      return state;
  }
};

/**
 * Byte-weighted aggregate progress across files in a session (0–100).
 * Cancelled files are excluded from the calculation.
 */
export const computeAggregateProgress = (files: UploadFileState[]): number => {
  const countable = files.filter((file) => file.status !== 'cancelled');
  if (countable.length === 0) {
    return 100;
  }

  if (countable.every((file) => file.status === 'complete')) {
    return 100;
  }

  const totalBytes = countable.reduce((sum, file) => sum + file.bytesTotal, 0);
  if (totalBytes === 0) {
    return 0;
  }

  const uploadedBytes = countable.reduce((sum, file) => {
    if (file.status === 'complete') {
      return sum + file.bytesTotal;
    }
    return sum + (file.progress / 100) * file.bytesTotal;
  }, 0);

  return Math.round((uploadedBytes / totalBytes) * 100);
};

/**
 * Whether a persisted S3 multipart registration has exceeded presigned URL TTL.
 * @param registeredAt - ISO timestamp from {@link PersistedMultipartContext}.
 */
export const isMultipartContextExpired = (
  registeredAt: string,
  now = Date.now()
) => now - new Date(registeredAt).getTime() > 60 * 60 * 1000;
